import { useApolloClient } from "@apollo/client";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { fetchZipCodeCities } from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  MILTIS_SOCIAL_REGIMES,
} from "utils/constants";
import { getDepartmentFromZipCode } from "utils/helpers/address";
import { getZoneDeltaStarts } from "utils/helpers/array";
import { hundredYearsAgoStart } from "utils/helpers/date";
import {
  bindDateInputProps,
  bindInputProps,
  clearNamedError,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

const ZIP_CODE_PATTERN = /^\d{5}$/;

function View({
  telecoms,
  contact,
  contract,
  onNext,
  onBack,
  deltaStart,
  insurancePlans,
  processId,
  territorial,
}) {
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_MILTIS.ADHERENT_BIRTHDATE", {
    returnObjects: true,
  });
  const SOCIAL_REGIMES_TRANSLATION = t("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });

  const { submitForm, values, ...formProps } = useFormik({
    initialValues: {
      telecoms,
      birthDate: get(contact, "user.birthDate") || null,
      socialRegime: get(contact, "socialRegime") || null,
      issuanceDate: get(contract, "issuanceDate") || null,
      optMadelin: get(contact, "optMadelin") || false,
    },
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        fetchZipCodeCities: (zip) => fetchZipCodeCities(client, zip),
      },
    ),
  });

  const isSelfEmployed =
    values.socialRegime === MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS;

  const zipCode = String(
    get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`) || "",
  ).trim();
  const { processDeltaStart, zoneDeltaStart } = ZIP_CODE_PATTERN.test(zipCode)
    ? getZoneDeltaStarts({
        insurancePlans,
        processId,
        department: getDepartmentFromZipCode(zipCode),
      })
    : {};
  const hasLongerZoneDelay =
    zoneDeltaStart != null &&
    processDeltaStart != null &&
    zoneDeltaStart > processDeltaStart;

  return (
    <Layout>
      <Header
        title={TRANSLATION.HEADER.TITLE}
        highlight={TRANSLATION.HEADER.HIGHLIGHT}
        subtitle={TRANSLATION.HEADER.SUBTITLE}
        onBack={onBack}
      />
      <div className="miltis-form">
        <div className="miltis-form__container">
          {/* Row 1: Code postal | Date de naissance */}
          <div className="miltis-form__row">
            <Input
              placeholder={TRANSLATION.FORM.ZIP_CODE.PLACEHOLDER}
              maxLength={5}
              label={TRANSLATION.FORM.ZIP_CODE.LABEL}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                values,
                ...formProps,
              })}
            />
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={TRANSLATION.FORM.BIRTH_DATE.PLACEHOLDER}
              label={TRANSLATION.FORM.BIRTH_DATE.LABEL}
              disabledDate={(current) =>
                current > dayjs().subtract(18, "years").endOf("year") ||
                current < hundredYearsAgoStart
              }
              defaultPickerValue={
                !get(values, "birthDate") && dayjs().subtract(30, "years")
              }
              {...bindDateInputProps({
                name: "birthDate",
                values,
                ...formProps,
                onBlur: (value) => {
                  const major = dayjs().subtract(18, "years").endOf("year");
                  const date = dayjs(value, DATE_FORMAT);
                  if (date?.isValid() && date.isBefore(major)) {
                    formProps.setFieldValue(
                      "birthDate",
                      date ? date.add(2, "h").toISOString() : null,
                    );
                  }
                },
              })}
            />
          </div>

          {/* Row 2: Régime social | Date d'effet souhaitée */}
          <div className="miltis-form__row">
            <Select
              options={Object.values(MILTIS_SOCIAL_REGIMES)
                .filter(
                  (option) =>
                    !territorial ||
                    option !==
                      MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS,
                )
                .map((option) => {
                  return {
                    label: SOCIAL_REGIMES_TRANSLATION[option] || option,
                    value: option,
                  };
                })}
              placeholder={TRANSLATION.FORM.SOCIAL_REGIME.PLACEHOLDER}
              label={TRANSLATION.FORM.SOCIAL_REGIME.LABEL}
              value={values.socialRegime}
              touched={get(formProps.touched, "socialRegime", false)}
              errors={get(formProps.errors, "socialRegime", "")}
              onChange={(value) => {
                formProps.setFieldValue("socialRegime", value);
                clearNamedError({
                  errors: formProps.errors,
                  setErrors: formProps.setErrors,
                  setFormikState: formProps.setFormikState,
                  name: "socialRegime",
                });
                // Reset optMadelin when social regime changes
                if (
                  value !== MILTIS_SOCIAL_REGIMES.SECURITE_SOCIALE_INDEPENDANTS
                ) {
                  formProps.setFieldValue("optMadelin", false);
                }
              }}
              onBlur={() => {
                formProps.setFieldTouched("socialRegime", true);
              }}
            />
            <Input
              type="date"
              format={DATE_FORMAT}
              placeholder={TRANSLATION.FORM.EFFECTIVE_DATE.PLACEHOLDER}
              label={TRANSLATION.FORM.EFFECTIVE_DATE.LABEL}
              disabledDate={(current) =>
                current < dayjs().add(deltaStart, "days").startOf("day")
              }
              {...bindDateInputProps({
                name: "issuanceDate",
                values,
                ...formProps,
              })}
            />
          </div>

          {hasLongerZoneDelay && (
            <p className="miltis-form__notice">
              {TRANSLATION.FORM.EFFECTIVE_DATE.OUTSIDE_AURA_NOTICE}
            </p>
          )}

          {/* {isSelfEmployed && (
            <div className="miltis-madelin-checkbox">
              <Checkbox
                variant="madelin"
                checked={values.optMadelin || false}
                onChange={(checked) =>
                  formProps.setFieldValue("optMadelin", checked)
                }
              >
                <div className="miltis-madelin-checkbox__content">
                  <p className="miltis-madelin-checkbox__text">
                    {TRANSLATION.FORM.MADELIN.TEXT}
                  </p>
                  <p className="miltis-madelin-checkbox__subtext">
                    {TRANSLATION.FORM.MADELIN.SUBTEXT}
                  </p>
                </div>
              </Checkbox>
            </div>
          )} */}

          <ActionButtons
            disabled={
              !get(values, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`) ||
              !values.birthDate ||
              !isEmpty(formProps.errors)
            }
            onNext={submitForm}
            // onBack={onBack}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
